import { FC, useCallback, useMemo, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import Lottie from "react-lottie"
import { Typography } from "@eventbrite/marmalade"

// redux
import { setMetaData } from "../../../store/slices/metadata.slice"

// utils
import { getFlag } from "../../../utils/query_param_flags"
import { navigateWithPrefix } from "../../../utils/general_utils"
import { processSearchResultsWithRelevance } from "../../../utils/search_relevance_utils"

// types
import { RootState } from "../../../store/store"
import { SingleHit } from "../../../services/help_articles.service"

// lottie data
import * as greenLoaderLottieData from "../../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

// styles
import {
  searchItemsContainer,
  searchItem,
  suggestedheader,
  searchLoadingContainer,
  noResultsContainer
} from "./SearchResultsBox.module.scss"

// Enhanced emphasis function that handles multiple keywords
export const addEmphasis = (query: string, title: string): string => {
  if (!query || !title) return title

  let highlightedTitle = title
  const queryWords = query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 1)

  // First try to highlight the exact phrase
  const exactPhraseRegex = new RegExp(`(${query})`, "gi")
  if (title.toLowerCase().includes(query.toLowerCase())) {
    return title.replace(exactPhraseRegex, "<strong>$1</strong>")
  }

  // Then highlight individual words
  queryWords.forEach((word) => {
    const wordRegex = new RegExp(`\\b(${word})\\b`, "gi")
    highlightedTitle = highlightedTitle.replace(
      wordRegex,
      "<strong>$1</strong>"
    )
  })

  return highlightedTitle
}

export const SearchResultsBox: FC<{
  query: string
  hits: SingleHit[]
  isSearchMenuOpen: boolean
}> = ({ hits, query, isSearchMenuOpen }) => {
  const dispatch = useDispatch()
  const [showNoResults, setShowNoResults] = useState(false)

  const langCode = useSelector((state: RootState) => state?.metaData?.langCode)
  const isLoadingResults = useSelector(
    (state: RootState) => state?.metaData?.isLoadingResults
  )

  // When query changes, show loader and decide "no results" after delay
  useEffect(() => {
    if (query) {
      dispatch(setMetaData({ isLoadingResults: true }))
      setShowNoResults(false)

      const timeoutId = setTimeout(() => {
        dispatch(setMetaData({ isLoadingResults: false }))
        if (!hits || hits.length === 0) {
          setShowNoResults(true)
        }
      }, 800) // delay before showing "No results"

      return () => clearTimeout(timeoutId)
    }
  }, [query, dispatch, hits])

  // If results arrive before timeout, stop loading and hide "no results"
  useEffect(() => {
    if (hits && hits.length > 0) {
      dispatch(setMetaData({ isLoadingResults: false }))
      setShowNoResults(false)
    }
  }, [hits, dispatch])

  const onClick = useCallback(
    (slug: string | undefined, articleId: string | undefined) => {
      if ((slug?.length ?? 0) > 0 && articleId) {
        navigateWithPrefix(`/${langCode}/articles/${articleId}/${slug}/`)
      }
    },
    [langCode]
  )

  const isUsingSearchTermFlag = useMemo(() => {
    const flag = getFlag()
    return flag === "searchterm"
  }, [])

  const searchOptions = useMemo(() => {
    if (!hits || hits.length === 0) {
      return null
    }

    const suggestedsearchHeader = (
      <Typography as="p" variant="body-sm" className={suggestedheader}>
        Suggested Articles
      </Typography>
    )

    // Use enhanced relevance-based processing and take only first 4 for dropdown
    const processedHits = processSearchResultsWithRelevance(hits, query)

    const resultItems = processedHits.slice(0, 4).map((x, i) => {
      const title = x.fields?.helparticletitle
      const slug = x.fields?.slug
      const articleId = x.fields?.articleid

      return (
        <li
          key={`${x.id}__${i}`}
          className={searchItem}
          onClick={() => onClick(slug, articleId)}
        >
          {!title ? (
            <div className={searchLoadingContainer}>
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: greenLoaderLottieData,
                  rendererSettings: {
                    preserveAspectRatio: "xMidYMid slice"
                  }
                }}
                width={24}
                height={24}
              />
            </div>
          ) : (
            <span
              dangerouslySetInnerHTML={{
                __html: addEmphasis(query, title)
              }}
            />
          )}
        </li>
      )
    })

    return [suggestedsearchHeader, ...resultItems]
  }, [query, hits, isUsingSearchTermFlag, onClick])

  if (!isSearchMenuOpen) {
    return null
  }

  return (
    <>
      {isLoadingResults && (
        <div className={searchLoadingContainer}>
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: greenLoaderLottieData,
              rendererSettings: {
                preserveAspectRatio: "xMidYMid slice"
              }
            }}
            width={24}
            height={24}
          />
        </div>
      )}

      {!isLoadingResults && hits && hits.length > 0 && (
        <ul
          id="search-autocomplete-suggestions"
          aria-label="search autocomplete"
          className={searchItemsContainer}
        >
          <Typography as="p" variant="body-md">
            {" "}
            {searchOptions}
          </Typography>
        </ul>
      )}
      {!isLoadingResults && showNoResults && query && (
        <div className={noResultsContainer}>
          <p>No results found for "{query}"</p>
        </div>
      )}
    </>
  )
}
